import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { ChevronsUpDown, ChevronUp, ChevronDown, Plus, Loader2, MoreVertical } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import usePermissions from '../../auth/usePermissions';
import notify from '../../utils/notify';

import '../../component-styles/RecordPages.css';
import QuickBooksConnectModal from '../../components/utils/QuickbooksConnectModal';
import { isQuickBooksConnectionError } from '../../utils/quickbooksErrors';
import ProposalFolioModal from '../../components/Projects/Proposals/ProposalFolioModal';
import ClientViewedBadge from '../../components/shared/ClientViewedBadge';
import RecordPagination from '../../components/shared/RecordPagination';
import ConfirmModal from '../../components/utils/ConfirmModal';
import TearSheetModal from '../../components/Projects/Proposals/TearSheetModal';
import TearSheetPdfDocument from '../../components/Projects/Proposals/TearSheetPdfDocument';
import ProposalPdfDocument from '../../components/Projects/Proposals/ProposalPdfDocument';
import ConvertToInvoiceModal from '../../components/Projects/Proposals/ConvertToInvoiceModal';
import ConvertToPoBlockersModal from '../../components/Projects/Proposals/ConvertToPoBlockersModal';
import folderIcon from '../../assets/folder-icon.png';
import API_BASE from '../../utils/api.js';
import { getConvertToPoBlockers, unassignedItemsNotice } from '../../utils/convertToPoMessage.js';
import { buildPublicProposalUrl } from '../../utils/publicLinks.js';
import { buildTearSheetPdf } from '../../utils/tearSheetPdf.js';
import { parseInvoiceDate } from '../../utils/invoiceAdjustments.js';
import { getProposalBalanceDue, getProposalInvoicingStatus } from '../../utils/proposalInvoicing.js';
import SendEmailModal from '../../components/common/SendEmailModal';
import usePageSize from '../../hooks/usePageSize';

/* ---------------------- Helpers ---------------------- */
function getApiBase() {
  return API_BASE;
}

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

function SortIcon({ active, order }) {
  if (!active) return <ChevronsUpDown size={14} className="sort-icon neutral" />;
  return order === 'asc' ? (
    <ChevronUp size={14} className="sort-icon active" />
  ) : (
    <ChevronDown size={14} className="sort-icon active" />
  );
}

// Converting to an invoice leaves the stored status alone (an approval still
// stands), so "Invoiced" / "Partially Invoiced" is layered on top here.
function getDisplayStatus(p) {
  return getProposalInvoicingStatus(p) || p.status;
}

// Invoiced items are collected on their invoice, not the proposal.
const getBalanceDue = getProposalBalanceDue;

function getQuickBooksEstimateUrl(estimateId, env = 'sandbox') {
  const base = env === 'production' ? 'https://qbo.intuit.com' : 'https://sandbox.qbo.intuit.com';

  return `${base}/app/estimate?txnId=${estimateId}`;
}

/* ---------------------- Dropdown ---------------------- */
function DropdownMenu({
  quickbooks,
  onSendToClient,
  onDownloadPDF,
  onDownloadTearSheets,
  onConvert,
  onConvertToPO,
  onCreateQBEstimate,
  onDelete,
  canEditCreate,
  loading,
}) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    function close(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const qbEstimateId = quickbooks?.estimateId;

  const handle = (fn) => () => {
    setOpen(false);
    fn();
  };

  return (
    <div className="menu-container" ref={ref}>
      <button
        className="menu-toggle"
        disabled={loading}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {loading ? <Loader2 size={16} className="spin" /> : <MoreVertical size={16} />}
      </button>

      <div className={`menu-dropdown ${open ? 'open' : ''}`}>
        <button onClick={handle(onSendToClient)}>Send to Client</button>
        <button onClick={handle(onDownloadPDF)}>Download PDF</button>
        <button onClick={handle(onDownloadTearSheets)}>Download Tear Sheets</button>

        {qbEstimateId && quickbooks?.environment && (
          <button onClick={() => { setOpen(false); window.open(getQuickBooksEstimateUrl(qbEstimateId, quickbooks.environment), '_blank'); }}>
            View in QuickBooks
          </button>
        )}
        {canEditCreate && !qbEstimateId && <button onClick={handle(onCreateQBEstimate)}>Create QuickBooks Estimate</button>}
        {canEditCreate && <button onClick={handle(onConvert)}>Convert to Invoice</button>}
        {canEditCreate && <button onClick={handle(onConvertToPO)}>Convert to Purchase Order</button>}

        {canEditCreate && (
          <button className="menu-danger" onClick={handle(onDelete)}>
            Delete Proposal
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------------------- Main Component ---------------------- */
export default function ProposalsSection({ projectId: propProjectId, project: propProject }) {
  const outlet = useOutletContext?.() || {};
  const projectId = propProjectId || outlet.projectId || '';
  const project = propProject || outlet.project || null;
  const navigate = useNavigate();

  const { permissions } = usePermissions();
  const canCreate = permissions.createEditInvoicesProposals !== false;
  const canApplyPayments = permissions.applyInvoiceProposalPayments !== false;

  const API_BASE = getApiBase();
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('idToken');

  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize('proposals');

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const [showQBModal, setShowQBModal] = useState(false);
  // The send that hit "not connected", replayed once QuickBooks is reconnected.
  const qbRetryRef = useRef(null);
  const [showFolio, setShowFolio] = useState(null);
  const [sendProposalModal, setSendProposalModal] = useState({ open: false, proposal: null, publicUrl: '', email: '' });
  const [vendorModal, setVendorModal] = useState({ open: false, proposal: null, vendors: [], unassignedCount: 0 });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', confirmLabel: 'Confirm', destructive: false, pendingAction: null });
  const [convertProposal, setConvertProposal] = useState(null);
  // { proposal, blockers } while Convert to PO is blocked
  const [poBlockers, setPoBlockers] = useState(null);

  const [companySettings, setCompanySettings] = useState(null);
  const [proposalTemplateSettings, setProposalTemplateSettings] = useState(null);
  const [loadingProposalId, setLoadingProposalId] = useState(null);
  const [tearSheetModal, setTearSheetModal] = useState(null);
  const [tearSheetRender, setTearSheetRender] = useState(null);
  const [proposalPdfRender, setProposalPdfRender] = useState(null);
  const tearSheetPdfRef = useRef(null);
  const proposalPdfRef = useRef(null);

  function askConfirm({ title, message, confirmLabel, destructive, action }) {
    setConfirmModal({ open: true, title, message, confirmLabel, destructive: !!destructive, pendingAction: action });
  }

  async function handleConfirm() {
    const action = confirmModal.pendingAction;
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
    if (action) await action();
  }

  function handleCancel() {
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
  }

  /* ---------------------- Fetch ---------------------- */
  const load = useCallback(
    async (bg = false) => {
      if (!userId || !projectId) return;
      bg ? setIsRefreshing(true) : setLoading(true);

      try {
        const res = await fetch(
          `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`
        );
        const data = await res.json();
        setProposals(Array.isArray(data) ? data : []);
      } catch {
        setProposals([]);
      } finally {
        bg ? setIsRefreshing(false) : setLoading(false);
      }
    },
    [API_BASE, userId, projectId]
  );

  useEffect(() => {
    load();
    const i = setInterval(() => load(true), 30000);
    return () => clearInterval(i);
  }, [load]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/settings/${encodeURIComponent(userId)}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setCompanySettings(data.company || null);
        setProposalTemplateSettings(data.proposalTemplate || null);
      })
      .catch(() => {});
  }, [userId]);

  async function fetchFullProposal(p) {
    setLoadingProposalId(p.proposalId);
    try {
      const res = await fetch(
        `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}/${encodeURIComponent(p.proposalId)}`
      );
      if (!res.ok) throw new Error('Failed to load proposal');
      return await res.json();
    } catch {
      notify('Failed to load proposal data.');
      return null;
    } finally {
      setLoadingProposalId(null);
    }
  }

  async function openTearSheets(p) {
    const data = await fetchFullProposal(p);
    if (!data) return;
    setTearSheetModal({ proposal: p, lineItems: data.lineItems || [] });
  }

  function handleTearSheetDownload({ title, options }) {
    // TearSheetPdfDocument routes images through the proxy itself.
    const items = tearSheetModal?.lineItems || [];
    setTearSheetModal(null);
    setTearSheetRender({ items, title, options });
  }

  useEffect(() => {
    if (!tearSheetRender || !tearSheetPdfRef.current) return;
    const el = tearSheetPdfRef.current;
    const safeTitle = tearSheetRender.title.replace(/[/\\?%*:|"<>]/g, '-');
    let cancelled = false;
    const imgs = Array.from(el.querySelectorAll('img'));
    Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
      )
    ).then(async () => {
      if (cancelled) return;
      try {
        const pdf = await buildTearSheetPdf(el);
        pdf.save(`${safeTitle}-TearSheets.pdf`);
      } catch (err) {
        console.error('Tear sheet PDF error', err);
        notify('Failed to generate tear sheets.');
      } finally {
        setTearSheetRender(null);
      }
    });
    return () => { cancelled = true; };
  }, [tearSheetRender]);

  async function downloadProposalPDF(p) {
    const data = await fetchFullProposal(p);
    if (!data) return;
    setProposalPdfRender({ proposal: { ...p, ...data } });
  }

  useEffect(() => {
    if (!proposalPdfRender || !proposalPdfRef.current) return;
    const el = proposalPdfRef.current;
    const p = proposalPdfRender.proposal;
    const filename = `Proposal-${p.proposalNumber || 'proposal'}.pdf`;
    let cancelled = false;
    const imgs = Array.from(el.querySelectorAll('img'));
    Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
      )
    ).then(() => {
      if (cancelled) return;
      html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#fff' },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        })
        .from(el)
        .save()
        .then(() => setProposalPdfRender(null));
    });
    return () => { cancelled = true; };
  }, [proposalPdfRender]);

  /* ---------------------- Totals ---------------------- */
  const totals = useMemo(() => {
    const sum = (f) => proposals.filter(f).reduce((s, p) => s + (p.totals?.grandTotal || 0), 0);
    return {
      total: sum(() => true),
      approved: sum((p) => p.status === 'approved'),
      pending: sum((p) => !p.invoiced && ['draft', 'sent', 'changes_requested'].includes(p.status)),
      balanceDue: proposals.reduce((s, p) => s + getBalanceDue(p), 0),
    };
  }, [proposals]);

  /* ---------------------- Sorting ---------------------- */
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const arr = proposals.filter(
      (p) =>
        !q ||
        p.proposalNumber?.toLowerCase().includes(q) ||
        p.title?.toLowerCase().includes(q) ||
        p.client?.name?.toLowerCase().includes(q) ||
        getDisplayStatus(p)?.toLowerCase().includes(q)
    );

    arr.sort((a, b) => {
      const get = (x) =>
        sortField === 'date'
          ? new Date(x.date || x.createdAt || x.updatedAt).getTime()
          : sortField === 'total'
            ? x.totals?.grandTotal || 0
            : (x[sortField] || '').toString().toLowerCase();

      return sortOrder === 'asc' ? get(a) - get(b) : get(b) - get(a);
    });

    return arr;
  }, [proposals, sortField, sortOrder, searchQuery]);

  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  function toggleSort(field) {
    if (field === sortField) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  }

  async function deleteProposal(p) {
    askConfirm({
      title: 'Delete Proposal',
      message: `Permanently delete proposal ${p.proposalNumber || ''}? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      action: async () => {
        try {
          const res = await fetch(
            `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
              projectId
            )}/${encodeURIComponent(p.proposalId)}`,
            { method: 'DELETE' }
          );

          if (!res.ok) {
            const data = await res.json();
            notify(data.error || 'Failed to delete proposal.');
            return;
          }

          load(true);
        } catch (err) {
          console.error(err);
          notify('Unable to delete proposal.');
        }
      },
    });
  }

  async function convertToInvoice(p) {
    if (getProposalInvoicingStatus(p) === 'invoiced') {
      notify('Every item on this proposal has already been invoiced.');
      return;
    }
    // The list refreshes on a timer, so pick items from the proposal as it is now.
    const data = await fetchFullProposal(p);
    if (!data) return;
    const proposal = { ...p, ...data };
    if (getProposalInvoicingStatus(proposal) === 'invoiced') {
      notify('Every item on this proposal has already been invoiced.');
      load(true);
      return;
    }
    setConvertProposal(proposal);
  }

  async function submitConversion(itemIds) {
    const p = convertProposal;
    if (!p?.proposalId) return;

    const res = await fetch(`${API_BASE}/api/proposals/convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        projectId,
        proposalId: p.proposalId,
        itemIds,
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      notify(data.error || 'Failed to convert proposal.');
      setConvertProposal(null);
      load(true);
      return;
    }

    const invoice = await res.json().catch(() => ({}));
    setConvertProposal(null);
    if (!invoice?.invoiceId) {
      notify("Your invoice was saved but couldn't be opened. Go to the Invoices section to find it, or contact support if it doesn't appear.");
      load(true);
      return;
    }

    navigate(`/projects/${projectId}/invoices/${invoice.invoiceId}`, {
      state: { project, invoice },
    });
  }

  async function convertToPO(p) {
    if (!p?.proposalId || !userId || !projectId) return;

    try {
      const preflight = await fetch(
        `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
          projectId
        )}/${encodeURIComponent(p.proposalId)}/convert-to-po`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dryRun: true }),
        }
      );
      const preflightData = await preflight.json().catch(() => ({}));
      if (!preflight.ok) {
        const blockers = getConvertToPoBlockers(preflightData);
        if (blockers.length > 0) {
          setPoBlockers({ proposal: p, blockers });
          return;
        }
        notify(preflightData.error || 'Failed to check vendors.');
        return;
      }
      setPoBlockers(null);

      if (Array.isArray(preflightData.vendors) && preflightData.vendors.length > 1) {
        setVendorModal({
          open: true,
          proposal: p,
          vendors: preflightData.vendors,
          unassignedCount: preflightData.unassignedCount || 0,
        });
        return;
      }

      askConfirm({
        title: 'Create Purchase Order',
        message:
          'Create a purchase order from this proposal? A new draft PO will be generated from the proposal line items.' +
          unassignedItemsNotice(preflightData.unassignedCount),
        confirmLabel: 'Create PO',
        action: async () => {
          try {
            const res = await fetch(
              `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
                projectId
              )}/${encodeURIComponent(p.proposalId)}/convert-to-po`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ splitByVendor: false }),
              }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
              notify(data.error || 'Failed to convert to purchase order.');
              return;
            }
            const poId = data.openedPoId || data.poIds?.[0] || data.poId;
            if (poId) {
              navigate(`/projects/${projectId}/purchase-orders/${poId}`, { state: { project } });
            }
          } catch (err) {
            console.error('Convert to PO error', err);
            notify('Unable to convert to purchase order.');
          }
        },
      });
    } catch (err) {
      console.error('Convert to PO error', err);
      notify('Unable to convert to purchase order.');
    }
  }

  async function confirmSplitVendors() {
    const p = vendorModal.proposal;
    if (!p?.proposalId) return;

    try {
      const res = await fetch(
        `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
          projectId
        )}/${encodeURIComponent(p.proposalId)}/convert-to-po`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ splitByVendor: true }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        notify(data.error || 'Failed to convert to purchase order.');
        return;
      }
      const poId = data.openedPoId || data.poIds?.[0] || data.poId;
      if (poId) {
        navigate(`/projects/${projectId}/purchase-orders/${poId}`, { state: { project } });
      }
    } catch (err) {
      console.error('Convert to PO error', err);
      notify('Unable to convert to purchase order.');
    } finally {
      setVendorModal({ open: false, proposal: null, vendors: [] });
    }
  }

  // For a client who approved some other way (email, a signed copy, paying in
  // QuickBooks). Picks the conversion back up once it's approved.
  async function markApprovedAndConvertToPO(p) {
    try {
      const res = await fetch(
        `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
          projectId
        )}/${encodeURIComponent(p.proposalId)}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ status: 'approved' }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        notify(data.error || 'Failed to mark the proposal approved.');
        return;
      }
    } catch (err) {
      console.error('Mark proposal approved error', err);
      notify('Failed to mark the proposal approved.');
      return;
    }

    const approved = { ...p, status: 'approved' };
    setProposals((prev) => prev.map((pr) => (pr.proposalId === p.proposalId ? approved : pr)));
    notify(`${p.proposalNumber || 'Proposal'} marked as approved.`);
    await convertToPO(approved);
  }

  const poBlockerActions = poBlockers
    ? {
        mark_approved: () => markApprovedAndConvertToPO(poBlockers.proposal),
        send_to_client: () => {
          setPoBlockers(null);
          sendToClient(poBlockers.proposal);
        },
        // Payments are taken on the proposal itself: open it with the payment
        // form ready for what's owed, and it carries on to the PO from there.
        ...(canApplyPayments && {
          record_payment: (blocker) =>
            navigate(`/projects/${projectId}/proposals/${poBlockers.proposal.proposalId}`, {
              state: {
                project,
                proposal: poBlockers.proposal,
                recordDeposit: { amount: blocker?.depositOutstanding },
              },
            }),
        }),
      }
    : null;

  async function createQBEstimate(p) {
    if (!token) {
      notify('You must be signed in.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/quickbooks/create-estimate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          proposalId: p.proposalId,
          projectId,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (isQuickBooksConnectionError(data)) {
          qbRetryRef.current = () => createQBEstimate(p);
          setShowQBModal(true);
          return;
        }

        notify(data.error || 'Failed to create QuickBooks estimate.');
        return;
      }

      load(true);
    } catch (err) {
      console.error(err);
      notify('Unable to connect to QuickBooks.');
    }
  }

  function sendToClient(p) {
    if (!p.shareToken) {
      notify('This proposal has no share link yet. Open it and save first.');
      return;
    }
    const publicUrl = buildPublicProposalUrl(window.location.origin, p.shareToken);
    const primaryEmail = p.client?.email || project?.email || '';
    setSendProposalModal({
      open: true,
      proposal: p,
      publicUrl,
      email: [...new Set([primaryEmail, project?.secondaryEmail].filter(Boolean))].join(', '),
    });
  }

  /* ---------------------- JSX ---------------------- */
  return (
    <section className="record-section">
      <div className="record-top-row">
        <h2 className="record-title">Proposals</h2>

        <div className="record-controls">
          <input
            className="record-search"
            placeholder="Search proposal #, client, status…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {canCreate && (
            <button
              className="record-add-btn"
              onClick={() =>
                navigate(`/projects/${projectId}/proposals/new`, {
                  state: { project },
                })
              }
            >
              <Plus size={14} /> Add New
            </button>
          )}
        </div>
      </div>

      <div className="record-card">
        <div className="record-metrics">
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.total)}</div>
            <div className="record-metric-label">Total Proposals</div>
          </div>
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.approved)}</div>
            <div className="record-metric-label">Approved</div>
          </div>
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.pending)}</div>
            <div className="record-metric-label">Pending</div>
          </div>
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.balanceDue)}</div>
            <div className="record-metric-label">Balance Due</div>
          </div>
        </div>

        {isRefreshing && (
          <div className="record-refresh-banner">
            <Loader2 className="spin" size={14} /> Refreshing…
          </div>
        )}

        {loading ? (
          <div className="record-empty">
            <div className="record-empty-icon" aria-hidden="true">
              ...
            </div>
            <div className="record-empty-title">Loading proposals…</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="record-empty">
            <div className="record-empty-icon" aria-hidden="true">
              [ ]
            </div>
            <div className="record-empty-title">No proposals available</div>
            <div className="record-empty-sub">Create your first proposal for this project.</div>
            {canCreate && (
              <button
                className="record-empty-cta"
                onClick={() =>
                  navigate(`/projects/${projectId}/proposals/new`, {
                    state: { project },
                  })
                }
              >
                <Plus size={14} /> Add New Proposal
              </button>
            )}
          </div>
        ) : (
          <div className="record-table-wrap">
          <div className="record-grid proposals-grid">
            <div className="record-grid-header">
              <div>Date</div>
              <div>Proposal #</div>
              <div>Client</div>
              <div>Tags</div>
              <div>Status</div>
              <div>Total</div>
              <div>Balance Due</div>
              <div>Folio</div>
              <div>Actions</div>
            </div>

            {paginated.map((p) => (
              <div key={p.proposalId} className="record-grid-row">
                <div>{parseInvoiceDate(p.date || p.createdAt || p.updatedAt)?.toLocaleDateString()}</div>
                <div>
                  {p.proposalNumber}
                  {p.title && <div className="record-doc-title">{p.title}</div>}
                </div>
                <div>{p.client?.name || '—'}</div>
                <div className="tags-inline">{p.tags?.join(', ') || '—'}</div>
                <div>
                  <div className="record-status-cell">
                    <StatusBadge status={getDisplayStatus(p)} />
                    <ClientViewedBadge
                      firstViewedAt={p.clientFirstViewedAt}
                      lastViewedAt={p.clientLastViewedAt}
                      viewCount={p.clientViewCount}
                      sent={!['draft', 'archived'].includes(p.status)}
                    />
                  </div>
                </div>
                <div>{formatCurrency(p.totals?.grandTotal)}</div>
                <div>{formatCurrency(getBalanceDue(p))}</div>
                <div>
                  <button
                    className="folio-icon-btn"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFolio(p);
                    }}
                  >
                    <img
                      src="/folder-icon.png"
                      alt="Open folio"
                      className="link-icon"
                      width={18}
                      height={18}
                      draggable={false}
                    />
                  </button>
                </div>
                <div className="actions-wrapper">
                  <button
                    className="primary-action"
                    onClick={() =>
                      navigate(`/projects/${projectId}/proposals/${p.proposalId}`, {
                        state: { project, proposal: p },
                      })
                    }
                  >
                    View Proposal
                  </button>

                  <DropdownMenu
                    quickbooks={p.quickbooks}
                    onSendToClient={() => sendToClient(p)}
                    onDownloadPDF={() => downloadProposalPDF(p)}
                    onDownloadTearSheets={() => openTearSheets(p)}
                    onConvert={() => convertToInvoice(p)}
                    onConvertToPO={() => convertToPO(p)}
                    onCreateQBEstimate={() => createQBEstimate(p)}
                    onDelete={() => deleteProposal(p)}
                    canEditCreate={canCreate}
                    loading={loadingProposalId === p.proposalId}
                  />
                </div>
              </div>
            ))}
          </div>
          </div>
        )}

        <RecordPagination
          page={currentPage}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {tearSheetModal && (
        <TearSheetModal
          defaultTitle={tearSheetModal.proposal?.client?.name || project?.customerName || project?.name || 'Tear Sheet'}
          onDownload={handleTearSheetDownload}
          onCancel={() => setTearSheetModal(null)}
          defaultShowVendor={proposalTemplateSettings?.showVendor === true}
        />
      )}

      {tearSheetRender && (
        <div className="ts-pdf-render-root" aria-hidden="true">
          <div ref={tearSheetPdfRef}>
            <TearSheetPdfDocument
              items={tearSheetRender.items}
              sheetTitle={tearSheetRender.title}
              company={companySettings}
              options={tearSheetRender.options}
            />
          </div>
        </div>
      )}

      {proposalPdfRender && (
        <div className="prop-pdf-render-root" aria-hidden="true">
          <div ref={proposalPdfRef}>
            <ProposalPdfDocument
              proposalNumber={proposalPdfRender.proposal.proposalNumber}
              computedItems={proposalPdfRender.proposal.lineItems || []}
              totals={proposalPdfRender.proposal.totals || { subtotal: 0, tax: 0, procurementFee: 0, grandTotal: 0 }}
              form={proposalPdfRender.proposal}
              project={project}
              company={companySettings}
              apiBase={API_BASE}
              template={proposalTemplateSettings}
              notes={proposalPdfRender.proposal.notes}
            />
          </div>
        </div>
      )}

      {/* ---------------- QuickBooks Modal ---------------- */}
      <QuickBooksConnectModal
        open={showQBModal}
        onClose={() => setShowQBModal(false)}
        onConnected={() => qbRetryRef.current?.()}
      />

      {showFolio && (
        <ProposalFolioModal
          proposal={showFolio}
          projectId={projectId}
          onClose={() => setShowFolio(null)}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        destructive={confirmModal.destructive}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {convertProposal && (
        <ConvertToInvoiceModal
          items={convertProposal.lineItems || []}
          paid={Number(convertProposal.totals?.paid || 0)}
          onCancel={() => setConvertProposal(null)}
          onConfirm={submitConversion}
        />
      )}

      {vendorModal.open && (
        <div
          className="proposal-lock-overlay"
          onClick={() => setVendorModal({ open: false, proposal: null, vendors: [] })}
        >
          <div className="proposal-lock-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Multiple vendors found</h3>
            <p>
              This proposal includes items from multiple vendors. Create one purchase order per
              vendor?
              {unassignedItemsNotice(vendorModal.unassignedCount)}
            </p>
            <div style={{ marginBottom: 10 }}>
              {vendorModal.vendors.map((v) => (
                <span
                  key={v}
                  style={{
                    display: 'inline-block',
                    padding: '4px 8px',
                    borderRadius: 999,
                    background: '#eef2ff',
                    color: '#1e1b4b',
                    fontSize: 12,
                    marginRight: 6,
                    marginBottom: 6,
                  }}
                >
                  {v}
                </span>
              ))}
            </div>
            <div className="proposal-lock-actions">
              <button
                className="if-btn-secondary"
                onClick={() => setVendorModal({ open: false, proposal: null, vendors: [] })}
              >
                Cancel
              </button>
              <button className="if-btn-primary" onClick={confirmSplitVendors}>
                Create Purchase Orders
              </button>
            </div>
          </div>
        </div>
      )}

      {poBlockers && (
        <ConvertToPoBlockersModal
          proposalNumber={poBlockers.proposal.proposalNumber}
          blockers={poBlockers.blockers}
          actions={poBlockerActions}
          onClose={() => setPoBlockers(null)}
        />
      )}

      {sendProposalModal.open && (
        <SendEmailModal
          type="proposal"
          defaultEmail={sendProposalModal.email}
          clientName={sendProposalModal.proposal?.client?.name || ''}
          docNumber={sendProposalModal.proposal?.proposalNumber || ''}
          docUrl={sendProposalModal.publicUrl}
          companyName={project?.companyName || project?.name || ''}
          onClose={() => setSendProposalModal({ open: false, proposal: null, publicUrl: '', email: '' })}
          onSent={async () => {
            notify(`Proposal sent to ${sendProposalModal.email}`);
            const p = sendProposalModal.proposal;
            if (p?.proposalId && p?.projectId && p?.status !== 'sent') {
              try {
                await fetch(
                  `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(p.projectId)}/${encodeURIComponent(p.proposalId)}/status`,
                  {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ status: 'sent' }),
                  }
                );
                setProposals((prev) =>
                  prev.map((pr) => pr.proposalId === p.proposalId ? { ...pr, status: 'sent' } : pr)
                );
              } catch {}
            }
          }}
        />
      )}
    </section>
  );
}

/* ---------------------- Status Badge ---------------------- */
function StatusBadge({ status }) {
  const colors = {
    draft: '#666',
    sent: '#1456d9',
    approved: '#0a7d33',
    changes_requested: '#b56a00',
    invoiced: '#0a7d33',
    partially_invoiced: '#0f766e',
    archived: '#6b7280',
  };

  const statusLabel = (status || 'draft').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

  return (
    <span
      style={{
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        background: `${colors[status] || '#ccc'}22`,
        color: colors[status] || '#333',
        textTransform: 'capitalize',
      }}
    >
      {statusLabel}
    </span>
  );
}

SortIcon.propTypes = { active: PropTypes.bool, order: PropTypes.string };
ProposalsSection.propTypes = {
  projectId: PropTypes.string,
  project: PropTypes.object,
};

